// import { Web3Storage } from 'web3.storage/dist/src/lib.d.ts'
import type { NftStorageConnector } from '@wovin/connect-nftstorage'
import type { EntityID } from '@wovin/core'
// import * as W3Up from '@web3-storage/w3up-client'
import { UcanStoreProxyConnector } from '@wovin/connect-ucan-store-proxy'
import { Logger } from 'besonders-logger'
import { createStore } from 'solid-js/store'
import * as W3Name from 'w3name'
import { getDecryptedSecretFromIDB } from '../data/agent/AgentCrypto'
import { useAgent } from '../data/agent/AgentState'
import { getProviderIDs } from '../data/storage-hooks.ts'
import { getVM } from '../data/VMs/MappedVMbase'
import { ProviderVM } from '../data/VMs/ProviderVM'
import { setStorageReachable, testStorageReachability } from '../ui/online-second'
import { useCurrentThread } from '../ui/reactive'
import { notifyToast } from '../ui/utils-ui'
import { getKuboApiUrl, hasKubo } from './kubo'
import { getSyncWorker } from './worker-utils'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars

export const DEMO_PROVIDER = 'https://demo-storage.note3.app'
export const parseW3Name = W3Name.parse

// let w3up: W3Up.Client

/**
 * Comma-separated list of public IPFS gateway base URLs used as a last-resort
 * IPNS resolver. Resolves IPNS → CID via the gateway's `X-Ipfs-Roots` header
 * (per the IPFS HTTP Gateway spec). Defaults to `https://ipfs.zt.ax` so the
 * app works out-of-the-box on any network where wovin-published IPNS names
 * are reachable via the public IPFS gateway network.
 */
const DEFAULT_IPNS_GATEWAYS = 'https://ipfs.zt.ax'
const PUBLIC_IPNS_GATEWAYS = (import.meta.env.VITE_PUBLIC_IPFS_GATEWAYS ?? DEFAULT_IPNS_GATEWAYS)
	.split(',')
	.map(s => s.trim().replace(/\/+$/, ''))
	.filter(Boolean)

/**
 * Comma-separated list of w3name-style naming service base URLs (`GET <base>/name/<ipns>`
 * → `{value: "/ipfs/<cid>"}`). Used as a graceful-transition fallback for IPNS names that
 * were published to w3name but haven't propagated to the public IPFS DHT (so public
 * gateways return `could not resolve name`). Defaults to `https://name.web3.storage`, which
 * is still live and holds historically-published note3 IPNS records. Set to an empty string
 * to disable once publishing reliably reaches the public DHT / ucan-store-proxy.
 */
const DEFAULT_IPNS_W3NAME_RESOLVERS = 'https://name.web3.storage'
const IPNS_W3NAME_RESOLVERS = (import.meta.env.VITE_IPNS_W3NAME_RESOLVERS ?? DEFAULT_IPNS_W3NAME_RESOLVERS)
	.split(',')
	.map(s => s.trim().replace(/\/+$/, ''))
	.filter(Boolean)

/**
 * Base URL of a naming service that supports WebSocket subscriptions to
 * `/name/<ipns>/watch` and HTTP GET on `/name/<ipns>`. Used by `IpnsWatcher`
 * for realtime IPNS updates. Overridable via env var; otherwise derived at
 * runtime from the configured `ucan-store-proxy` provider (see `initStorage`),
 * falling back to the demo provider if none is configured. Plain IPFS
 * gateways (e.g. ipfs.zt.ax) do NOT implement `/name/*` or support WebSocket
 * subscriptions, so pointing this at a gateway makes the watcher fail at
 * runtime; it's opt-in realtime and falls back to periodic polling via
 * `resolveIPNS` regardless.
 */
const IPNS_WATCHER_URL_OVERRIDE = import.meta.env.VITE_IPNS_WATCHER_URL?.trim().replace(/\/+$/, '')

const [storageState, setStorageState] = createStore({
	nftStorage: null,
	ucanUploads: null,
	gateways: null,
	ipnsGatewayResolvers: PUBLIC_IPNS_GATEWAYS,
	ipnsW3NameResolvers: IPNS_W3NAME_RESOLVERS,
	ipnsWatcherUrl: IPNS_WATCHER_URL_OVERRIDE ?? DEMO_PROVIDER,
} as {
	nftStorage: NftStorageConnector | null
	ucanUploads: UcanStoreProxyConnector[] | null
	gateways: ProviderVM[]
	ipnsGatewayResolvers: string[]
	ipnsW3NameResolvers: string[]
	ipnsWatcherUrl: string
})
export { storageState }

export async function initStorage() {
	LOG('Initializing Storage...')

	const thread = useCurrentThread()
	let providers = getProviderIDs(undefined, thread).map((id: EntityID) => getVM(ProviderVM, id, thread))
	LOG(`[initStorage] providers`, providers)
	// HACK ipfs.io never has our CIDs... so why bother?
	// if (!providers.some(({ type }) => ['ucan-store-proxy', 'ipfs-gateway'].includes(type))) { // TODO: replace with hasCapability('retrieve')
	// 	ProviderVM.buildNew({ type: 'ipfs-gateway', name: 'IPFS.io Gateway', url: 'https://ipfs.io' }).commit(thread)
	// }
	if (!providers.some(({ type, url }) => type === 'ucan-store-proxy' || url?.includes(DEMO_PROVIDER))) { // TODO: use better logic
		ProviderVM.buildNew({ type: 'ucan-store-proxy', name: 'Note3 Demo Storage', url: DEMO_PROVIDER }).commit(thread)
	}
	// TODO refactor to proper newage reactivity
	providers = getProviderIDs(undefined, thread).map(id => getVM(ProviderVM, id, thread)) // HACK refresh list after maybe adding some
	const ucanProvider = providers.find(({ type }) => type === 'ucan-store-proxy')
	setStorageState({
		gateways: providers.filter(({ type }) => ['ucan-store-proxy', 'ipfs-gateway'].includes(type)),
		ipnsWatcherUrl: IPNS_WATCHER_URL_OVERRIDE ?? ucanProvider?.url ?? DEMO_PROVIDER,
	})
	DEBUG(`[initStorage] providers after check:`, { providers, storageState })

	await initUcanUpload()

	setTimeout(async () => setStorageReachable(await testStorageReachability()))
}

export async function initUcanUpload() {
	const agent = useAgent()
	// Capture the thread synchronously while the caller's withThread() scope is
	// still active. The detached IIFE below resumes across `await`s, where the
	// thread scope/Solid context is no longer available — so we must pass `thread`
	// explicitly to getProviderIDs()/getVM() instead of letting them fall back to
	// useThread(), otherwise later loop iterations throw "no thread".
	const thread = useCurrentThread()
	const ucanProviderIDs = getProviderIDs('ucan-store-proxy', thread)
	void (async () => { // HACK: we're not awaiting...
		// Detached + unawaited: wrap the whole body so any failure (e.g. a provider
		// init rejecting) is logged instead of becoming an unhandled rejection.
		// Storage init is non-critical — the app/pub still renders without it.
		try {
			DEBUG(`Found UCAN storage providers?`, { providerIDs: ucanProviderIDs })
			const providers: UcanStoreProxyConnector[] = []
			for (const providerID of ucanProviderIDs) {
				const url = getVM(ProviderVM, providerID, thread).url
				if (url) {
					const ucanUpload = await UcanStoreProxyConnector.init({
						url,
						issuerKey: await agent.getEdDSAsigningKeypair(),
					})
					providers.push(ucanUpload)
					// ? localStorage.setItem('storage.ucan-proxy.ucan', ucan.encode(nftStorage.rootUcan))
					DEBUG(`Got UcanUpload`, { ucanUpload, hasStorageSetup: agent.hasStorageSetup })
				}
			}
			setStorageState({ ucanUploads: providers })

			// Sync storage config to SyncWorker so it can do push/pull I/O
			try {
				const signerSecret = await getDecryptedSecretFromIDB(agent)
				const SyncWorker = getSyncWorker()
				await SyncWorker.initStorageInWorker({
					providers: providers.map(p => ({ url: p.baseUrl })),
					signerSecret,
					kuboUrl: hasKubo() ? getKuboApiUrl() : null,
				})
			} catch (err) {
				WARN('[initUcanUpload] failed to init storage in worker', err)
			}
		} catch (err) {
			WARN('[initUcanUpload] failed', err)
		}
	})()
	// reaction(() => storageProviderIDs[0], () => {
	// 	LOG(`Storage provider changed`)
	// })
}
export async function logoutNftStorage() {
	LOG(`Logout of NftStorage`)
	setStorageState({ nftStorage: null })
	localStorage.setItem('storage.nftstorage.api_key', '')
	localStorage.setItem('storage.nftstorage.ucan', '')
}

export function createHandleAuthFx({ w3, email, setRegisteringSpace, tryRegisterSpace, setSubmitted, keyring }) {
	return async (e) => {
		DEBUG('Submit:', e, email())
		e.preventDefault()
		setSubmitted(true)
		try {
			await w3.authorize(email() as `${string}@${string}`)
			DEBUG('Authorized!', keyring, w3)
			if (!keyring.spaces.length) {
				DEBUG('Creating space...')
				const did = await w3.createSpace('note3-dev')
				DEBUG('Created space', did)
				// await setCurrentSpace(did)
			}
			if (!keyring.space.registered()) {
				setRegisteringSpace(true)
				DEBUG('Space is unregistered, registering', keyring.space)
				let tries = 0
				// HACK to work around https://github.com/web3-storage/w3ui/issues/257
				const tryRegisterInLoop = async () => {
					try {
						await tryRegisterSpace()
					} catch (error) {
						console.error(`failed to register space with ${email()}:`, error)
						if (tries < 10) {
							tries++
							await new Promise((resolve, reject) => setTimeout(resolve, 500 * tries))
							await tryRegisterInLoop()
						} else {
							alert("Failed to register space - you can try using the 'TryFix' button in settings")
						}
					}
				}
				await tryRegisterInLoop()
			}
			DEBUG('Got a space!', { keyring, space: keyring.space })
		} catch (err) {
			// throw new Error('failed to authorize', { cause: err })
			ERROR(`failed to authorize ${email()}`, err)
			notifyToast(`Failed to authorize web3storage: ${(err as any).message}`, 'danger')
		} finally {
			setRegisteringSpace(false)
			setSubmitted(false)
		}
	}
}

export async function initIPNS() {
	// TODO get array of my ipns names from localstorage #9
	// TODO check if there is roothash in the URL and check if its mine #5
	// if so use it, otherwise use newest from localstorage, otherwise create new
	// const allPublications = await stateDB.publications.toArray()
	// // const pubFromIDB = (publicationThatsFocused && allPublications?.find(p => p.id === publicationThatsFocused)) || allPublications?.[0]
	// const agent = useAgent()
	// // const { agentString: appAgent, ag } = agent
	// const pubSigningKeyBytes = await agent.crypto?.keystore?.publicWriteKey()
	// const pubEncryptionKeyBytes = await agent.crypto?.keystore?.publicExchangeKey()
	// DEBUG('[odd]', { pubSigningKeyBytes, pubEncryptionKeyBytes })
	// const pubKeyCryptoReimported = await importRsaKey(pubEncryptionKeyBytes, ['encrypt'])
	// const enc = new TextEncoder()
	// const dec = new TextDecoder('utf-8')
	// const testPayload = enc.encode('fission is rather odd')
	// const encPayloadKeystore = await agent.crypto?.rsa.encrypt(testPayload, pubKeyCryptoReimported)
	// const deencPayloadKeystore = dec.decode(await agent.crypto?.keystore.decrypt(encPayloadKeystore))
	// DEBUG('[odd]', { pubSigningKeyBytes, pubEncryptionKeyBytes, pubKeyCryptoReimported, encPayloadKeystore, deencPayloadKeystore })

	// const exportedKey = await state.crypto?.rsa?.exportPublicKey(pubExchangeKey)

	// TODO refactor this to live elsewhere
	// if (pubFromIDB) {
	// 	name = await W3Name.from(pubFromIDB.pk)
	// 	await stateDB.publications.update(name.toString(), { ts: dateNowIso(), appAgent })
	// 	DEBUG('Loaded name: ', name.toString())
	// } else {
	// 	name = await W3Name.create()
	// 	const { key, key: { bytes: pk } } = name
	// 	const nameString = name.toString()
	// 	DEBUG('created new name: ', nameString)
	// 	//TODO: make default encrypted
	// 	await stateDB.publications.put({ id: nameString, name: 'default', pk, createdAt: dateNowIso(), publishedBy: appAgent, lastPush: null })
	// 	// const signedPubName = await key.sign(name.bytes)
	// 	// const encodedSig = ab2str(signedPubName)
	// 	// insertApplogsIDB([
	// 	// 	{ en: nameString, at: 'pub/sig', vl: encodedSig, ag },
	// 	// ])
	// }
	// updateAgentState({ w3NamePublic: name })
}

// TODO make uploader a global reactively available instance
// ooo sounds nice
export async function storeCar(car: Blob, uploader: any) {
	try {
		// upload to web3.storage using putCar
		LOG('Uploading', car, 'using', uploader)
		const cid = await uploader.uploadCAR(
			car, /* {
				name: 'putCar using dag-json',
				// include the dag-json codec in the decoders field
				decoders: [json],
			} */
		)
		DEBUG('Stored dag-json:', cid.toString())
		return cid
	} catch (err) {
		ERROR(`[storeCar]`, err)
		throw new Error(`Failed to upload: ${(err as any)?.message}\ncheck storage settings`)
	}
}

// TODO make some more sexy store or state or signalDepot or something
export const getW3NamePublic = () => useAgent().w3NamePublic?.toString()
